import { useState, useEffect } from "react";
import moment from "moment-timezone";
import {
  GenerateLoadingOrError,
  genIdOptionList,
  CommonDestinationTimezone,
  createDateLabelWith,
} from "../../lib/utils";
import {
  ArchiveIcon,
  ArrowUpToLineIcon,
  ListFilter as ListFilterIcon,
  Loader,
  Trash2Icon,
} from "lucide-react";
import { DataTable, type DataTableColumn, type RowBgColorResult, type DataTableLegendItem, dateSort, dateWithFormatSort } from "@/components/ui/DataTable/index";
import { CommodityOptions } from "../../lib/api/commodity";
import { useListLocations, Location } from "../../lib/api/generalContract";
import { useListTraders } from "../../lib/api/trader";
import { formatNumberToDisplay, formulateOptionMonths } from "../../lib/api/common";
import { useListCustomers } from "../../lib/api/customer";
import { safeParseDecimal } from "@/lib/excel";
import { Trader, Customer } from "../../lib/api/reports";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { useGetDirectSalesSummary, useArchiveDirectSales, useListDirectSales, DirectSale } from "@/lib/api/directSales";
import { Tabs, TabsContent, TabsTrigger, TabsList } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { CreateFormModal, UpdateFormModal } from "./modals";
import { ConfirmDeleteModal } from "./confirmDeleteModal";
import { RecentReportDialog } from "./recentReportDialog";
import { BreakdownReportDialog } from "./breakdownReportDialog";
import { OpenReportDialog } from "./openReportDialog";
import { SummaryTable } from "./summaryTable";
import { Decimal } from "decimal.js";

interface FlattenedDirectSaleLineItem {
  /** The parent DirectSale's id — used for grouping */
  direct_sale_id: string;
  /** Reference to the full parent DirectSale for passing to modals/actions */
  _parentDirectSale: DirectSale;

  // Parent-level fields (duplicated on every line item row for aggregation display)
  location_id: string;
  producer_id: string;
  trader_id: string;
  has_placeholder_data: boolean;
  contract_type: string;
  commodity: string;
  crop_year: number;
  contract_id: string;
  gl_date: Date;
  contract_date: Date;
  destination: string;
  delivery_start_date: Date;
  delivery_end_date: Date;
  futures_month: string;
  notes?: string;
  offset_against_ap_settlement?: boolean;
  should_send_ar_invoice?: boolean;
  Location?: { long_name: string };
  Producer?: { long_name: string };
  Trader?: { name: string };

  // Line-item-level fields
  line_item_qty: Decimal;
  line_item_uom: string;
  line_item_futures: Decimal;
  line_item_basis: Decimal;
  line_item_net_cash_price: Decimal;
  line_item_average_cash_price: Decimal | null;
  line_item_marketing_fee_rate: Decimal;
  line_item_marketing_fee: Decimal;
  line_item_has_placeholder_data: boolean;
}

function flattenDirectSales(contracts: DirectSale[]): FlattenedDirectSaleLineItem[] {
  const rows: FlattenedDirectSaleLineItem[] = [];
  for (const sale of contracts) {
    for (const li of sale.LineItems) {
      rows.push({
        direct_sale_id: sale.id ?? "",
        _parentDirectSale: sale,

        location_id: sale.location_id,
        producer_id: sale.producer_id,
        trader_id: sale.trader_id,
        has_placeholder_data: sale.has_placeholder_data,
        contract_type: sale.contract_type,
        commodity: sale.commodity,
        crop_year: sale.crop_year,
        contract_id: sale.contract_id,
        gl_date: sale.gl_date,
        contract_date: sale.contract_date,
        destination: sale.destination,
        delivery_start_date: sale.delivery_start_date,
        delivery_end_date: sale.delivery_end_date,
        futures_month: sale.futures_month,
        notes: sale.notes,
        offset_against_ap_settlement: sale.offset_against_ap_settlement,
        should_send_ar_invoice: sale.should_send_ar_invoice,
        Location: (sale as unknown as Record<string, unknown>).Location as FlattenedDirectSaleLineItem["Location"],
        Producer: (sale as unknown as Record<string, unknown>).Producer as FlattenedDirectSaleLineItem["Producer"],
        Trader: (sale as unknown as Record<string, unknown>).Trader as FlattenedDirectSaleLineItem["Trader"],

        line_item_qty: li.qty,
        line_item_uom: li.uom,
        line_item_futures: li.futures,
        line_item_basis: li.basis,
        line_item_net_cash_price: li.net_cash_price,
        line_item_average_cash_price: li.average_cash_price,
        line_item_marketing_fee_rate: li.marketing_fee_rate,
        line_item_marketing_fee: li.marketing_fee,
        line_item_has_placeholder_data: li.has_placeholder_data,
      });
    }
  }
  return rows;
}

interface FilterState {
  producer_id?: string;
  commodity?: string;
  futures_month?: string;
  gl_date?: string;
  entry_start_date?: string;
  entry_end_date?: string;
}

function generateStatusFilterFromTab(tab: string) {
  switch (tab) {
    case "open":
      return "open";
    case "closed":
      return "closed";
    default:
      return undefined;
  }
}

export function DirectSalesDashboard() {
  const { toast } = useToast();
  const [loadingRows, setLoadingRows] = useState<Record<string, 'create' | 'update' | 'delete'>>({});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<"summary" | "open" | "closed">("open");
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [deletingDirectSaleData, setDeletingDirectSaleData] = useState<undefined | DirectSale>(undefined);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [openReportDialogOpen, setOpenReportDialogOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [updateFormData, setUpdateFormData] = useState<DirectSale | null>(null);
  const locationRes = useListLocations();
  const customersResult = useListCustomers({ minimal: true });
  const listTradersRes = useListTraders();
  const summaryRes = useGetDirectSalesSummary();
  const contractsRes = useListDirectSales({
    ...filters,
    gl_date: filters.gl_date ? moment.tz(moment(filters.gl_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).toDate() : undefined,
    entry_date_range: filters.entry_start_date && filters.entry_end_date ? {
      start: moment.tz(moment(filters.entry_start_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).toDate(),
      end: moment.tz(moment(filters.entry_end_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).toDate()
    } : undefined,
    status: generateStatusFilterFromTab(activeTab),
  });
  const loadingOrErrorUi = GenerateLoadingOrError([
    summaryRes,
    locationRes,
    customersResult,
    contractsRes,
    listTradersRes,
  ]);
  const updateDirectSaleStatus = useArchiveDirectSales();

  useEffect(() => {
    if (filterDialogOpen) {
      setTempFilters({ ...filters });
    }
  }, [filterDialogOpen, filters]);

  if (loadingOrErrorUi) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Direct Sales
        </h1>
        {loadingOrErrorUi}
      </section>
    );
  }

  const locations = locationRes.data?.data || [];
  const customers = customersResult.data?.data || [];
  const contracts = contractsRes.data?.data?.data || [];
  const traders = listTradersRes.data?.data || [];
  const summary = summaryRes.data?.data || [];

  const handleApplyFilters = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (
      (!tempFilters.entry_start_date && !!tempFilters.entry_end_date) ||
      (!!tempFilters.entry_start_date && !tempFilters.entry_end_date)
    ) {
      toast({
        title: "Invalid Date Range",
        description: "Must specify both start and end dates.",
        variant: "destructive",
      });
      return;
    }

    if (tempFilters.entry_start_date && tempFilters.entry_end_date) {
      const start = moment(tempFilters.entry_start_date);
      const end = moment(tempFilters.entry_end_date);
      if (end.isBefore(start)) {
        toast({
          title: "Invalid Date Range",
          description: "Entry end date cannot be before the start date.",
          variant: "destructive",
        });
        return;
      }
    }

    setFilters({
      ...tempFilters,
    });
    setFilterDialogOpen(false);
  };

  const handleResetFilters = () => {
    setTempFilters({});
  };

  const removeFilter = (key: keyof FilterState) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
  };

  const clearAllFilters = () => {
    setFilters({});
    setTempFilters({});
  };

  const getCustomerNameById = (id: string) => customers.find((c: Customer) => c.id === id)?.long_name || id;
  const getCommodityLabel = (value: string) => CommodityOptions.find(c => c.value === value)?.label || value;
  const getFuturesMonthLabel = (value: string) => formulateOptionMonths().find(m => m.value === value)?.label || value;
  const formatDateForDisplay = (dateString?: string) => dateString ? moment(dateString).format('MM/DD/YYYY') : '';
  const formatMonthForDisplay = (dateString?: string) => dateString ? moment(dateString).format('MMM YYYY') : '';

  const onClose = (newStatus: "open" | "closed") => async (data: any) => {
    if (isClosing) {
      return;
    }
    setIsClosing(true);

    try {
      const result = await updateDirectSaleStatus({ id: data.id, status: newStatus });
      if (!result.success) {
        throw result;
      }

      toast({
        title: `Updated Direct Sale`,
        description: `Marked as ${newStatus}`,
      });
    } catch (error: any) {
      console.error("Failed to update Direct Sale Status", error);

      toast({
        title: "Failed to close Direct Sale",
        description: "",
      });
    } finally {
      setIsClosing(false);
    }
  };

  const onDelete = async (data: DirectSale) => {
    setDeletingDirectSaleData(data);
  };

  const flattenedContracts = flattenDirectSales(contracts);

  /** Aggregation that returns the first leaf row's value — since all leaf rows in a
   *  direct_sale_id group share the same parent, this gives the correct parent value
   *  and allows TanStack Table to sort group rows by it. */
  const parentFieldAggregation: DataTableColumn<FlattenedDirectSaleLineItem>["aggregationFn"] =
    (_columnId, _leafRows, childRows) => {
      return childRows[0]?.getValue(_columnId) ?? null;
    };

  const getParentFromRow = (row: { subRows: { original: FlattenedDirectSaleLineItem }[] }): DirectSale | undefined =>
    row.subRows[0]?.original?._parentDirectSale;

  const getParentField = <K extends keyof DirectSale>(row: { subRows: { original: FlattenedDirectSaleLineItem }[] }, key: K): DirectSale[K] | undefined =>
    getParentFromRow(row)?.[key];

  const generateColumns: (newStatus: "open" | "closed") => DataTableColumn<FlattenedDirectSaleLineItem>[] = (newStatus: "open" | "closed") => [
    {
      id: "direct_sale_id",
      accessorKey: "direct_sale_id",
      header: "Group",
      enableGrouping: true,
      enableSorting: false,
      enableFiltering: false,
      cell: () => null,
      aggregatedCell: ({ row }) => {
        return "";
      },
    },
    {
      id: "actions",
      header: "Actions",
      minSize: 110,
      maxSize: 110,
      cell: () => null,
      aggregatedCell: ({ row }) => {
        const parentSale = getParentFromRow(row);
        if (!parentSale) return null;
        const rowId = parentSale.id ?? "";
        const loadingState = loadingRows[rowId];
        const isAnyLoading = !!loadingState;

        return (
          <div className="flex gap-4 flex-row items-center flex-nowrap">
            <Button
              size="icon"
              variant="outline"
              onClick={() => onClose(newStatus)(parentSale)}
              disabled={isAnyLoading}
              className="h-6 w-6"
              title={newStatus === "open" ? "Un-Archive" : "Archive"}
            >
              {loadingState === 'create' ? <Loader className="h-4 w-4 animate-spin" /> : newStatus === "open" ? <ArrowUpToLineIcon className="h-4 w-4" /> : <ArchiveIcon className="h-4 w-4" />}
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onDelete(parentSale)}
              disabled={isAnyLoading}
              className="h-6 w-6"
              title="Delete"
            >
              {loadingState === 'create' ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2Icon className="h-4 w-4" />}
            </Button>
          </div>
        );
      },
      disableRowClick: true,
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: "type",
      header: "Type",
      accessorFn: (row) => row.line_item_has_placeholder_data ? "Other" : "Flat",
      aggregationFn: (_columnId, leafRows) => {
        const parent = leafRows[0]?.original?._parentDirectSale;
        return parent?.has_placeholder_data ? "Other" : "Flat";
      },
      aggregatedCell: ({ getValue }) => getValue() as string,
    },
    {
      id: "gl_date",
      accessorKey: "gl_date",
      header: "GL Date",
      cell: (info) => createDateLabelWith({ field: info.getValue() as string, outputFormat: "MMM YYYY" }),
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        if (!parent) return "";
        return createDateLabelWith({ field: parent.gl_date as unknown as string, outputFormat: "MMM YYYY" });
      },
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("MMM YYYY"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      minSize: 120,
      maxSize: 120,
    },
    {
      id: "contract_date",
      accessorKey: "contract_date",
      header: "Entry Date",
      size: 80,
      cell: (info) => createDateLabelWith({ field: info.getValue() as string }),
      sortinFn: dateWithFormatSort("MM/DD/YYYY"),
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        if (!parent) return "";
        return createDateLabelWith({ field: parent.contract_date as unknown as string, outputFormat: 'MM/DD/YYYY' });
      },
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      minSize: 130,
      maxSize: 130,
    },
    {
      id: "location_id",
      accessorFn: (row) => row.Location?.long_name || "",
      header: "Location",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        const loc = (parent as unknown as Record<string, unknown>)?.Location as FlattenedDirectSaleLineItem["Location"] | undefined;
        return loc?.long_name || "";
      },
      minSize: 160,
      maxSize: 160,
    },
    {
      id: "producer_id",
      accessorFn: (row) => row.Producer?.long_name || "",
      header: "Producer",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        const prod = (parent as unknown as Record<string, unknown>)?.Producer as FlattenedDirectSaleLineItem["Producer"] | undefined;
        return prod?.long_name || "";
      },
      minSize: 160,
      maxSize: 180,
    },
    {
      id: "commodity",
      accessorFn: (row) => {
        const commodity_value = row.commodity || "";
        return CommodityOptions.find((option) => option.value === commodity_value)?.label || commodity_value;
      },
      header: "Commodity",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const commodity_value = getParentField(row, "commodity") || "";
        return CommodityOptions.find((option) => option.value === commodity_value)?.label || commodity_value;
      },
      minSize: 80,
      maxSize: 130,
    },
    {
      id: "crop_year",
      accessorKey: "crop_year",
      header: "Crop Year",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "crop_year") ?? "",
      minSize: 160,
      maxSize: 160,
    },
    {
      id: "line_item_qty",
      accessorKey: "line_item_qty",
      header: "Quantity",
      minSize: 160,
      maxSize: 160,
      cell: (info) => formatNumberToDisplay(info.getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      aggregationFn: (_columnId, leafRows) => {
        let total = new Decimal(0);
        for (const row of leafRows) {
          total = total.plus(new Decimal(row.original.line_item_qty?.toString() ?? "0"));
        }
        return total;
      },
      aggregatedCell: ({ getValue }) => formatNumberToDisplay(getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      id: "line_item_average_cash_price",
      accessorKey: "line_item_average_cash_price",
      header: "Average Cash Price",
      minSize: 160,
      maxSize: 160,
      cell: (info) => formatNumberToDisplay(info.getValue() as Decimal | null, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      aggregationFn: (_columnId, leafRows) => {
        const rows = leafRows.filter(row => !row.original.has_placeholder_data && row.original.line_item_net_cash_price);
        return rows.reduce((acc, item) => acc.plus(item.original.line_item_net_cash_price ?? 0), new Decimal(0)).div(rows.length || 1).toDecimalPlaces(4).toFixed();
      },
      aggregatedCell: (info) => info.getValue(),
    },
    {
      id: "line_item_net_cash_price",
      accessorKey: "line_item_net_cash_price",
      header: "Net Cash Price",
      minSize: 160,
      maxSize: 160,
      cell: (info) => formatNumberToDisplay(info.getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      aggregationFn: (_columnId, leafRows) => {
        const rows = leafRows.filter(row => !row.original.has_placeholder_data && row.original.line_item_net_cash_price);
        return rows.reduce((acc, item) => acc.plus(item.original.line_item_net_cash_price ?? 0), new Decimal(0)).div(rows.length || 1).toDecimalPlaces(4).toFixed();
      },
      aggregatedCell: (info) => info.getValue(),
    },
    {
      id: "line_item_marketing_fee",
      accessorKey: "line_item_marketing_fee",
      header: "Marketing Fee",
      minSize: 160,
      maxSize: 160,
      cell: (info) => formatNumberToDisplay(info.getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      aggregationFn: (_columnId, leafRows) => {
        let total = new Decimal(0);
        for (const row of leafRows) {
          total = total.plus(new Decimal(row.original.line_item_marketing_fee?.toString() ?? "0"));
        }
        return total;
      },
      aggregatedCell: ({ getValue }) => formatNumberToDisplay(getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      id: "line_item_uom",
      accessorKey: "line_item_uom",
      header: "Uom",
      aggregationFn: () => "",
      aggregatedCell: () => "",
      minSize: 80,
      maxSize: 80,
    },
    {
      id: "destination",
      accessorKey: "destination",
      header: "Destination",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "destination") || "",
      minSize: 160,
      maxSize: 160,
    },
    {
      id: "trader_id",
      accessorFn: (row) => row.Trader?.name || "",
      header: "Trader",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        const trader = (parent as unknown as Record<string, unknown>)?.Trader as FlattenedDirectSaleLineItem["Trader"] | undefined;
        return trader?.name || "";
      },
      minSize: 160,
      maxSize: 160,
    },
    {
      id: "notes",
      accessorKey: "notes",
      header: "Notes",
      size: 210,
      minSize: 100,
      enableTooltip: true,
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "notes") || "",
    },
    {
      id: "contract_id",
      accessorKey: "contract_id",
      header: "Contract ID",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "contract_id") || "",
      minSize: 160,
      maxSize: 160,
    },
    {
      id: "futures_month",
      accessorKey: "futures_month",
      header: "Futures Month",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "futures_month") || "",
      minSize: 160,
      maxSize: 160,
    },
    {
      id: "delivery_start_date",
      accessorKey: "delivery_start_date",
      header: "Delivery Start Date",
      minSize: 160,
      maxSize: 160,
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("MMM YYYY"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      cell: (info) => createDateLabelWith({ field: info.getValue() as string, outputFormat: "MMM YYYY" }),
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        if (!parent) return "";
        return createDateLabelWith({ field: parent.delivery_start_date as unknown as string, outputFormat: "MMM YYYY" });
      },
    },
    {
      id: "delivery_end_date",
      accessorKey: "delivery_end_date",
      header: "Delivery End Date",
      minSize: 160,
      maxSize: 160,
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("MMM YYYY"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      cell: (info) => createDateLabelWith({ field: info.getValue() as string, outputFormat: "MMM YYYY" }),
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => {
        const parent = getParentFromRow(row);
        if (!parent) return "";
        return createDateLabelWith({ field: parent.delivery_end_date as unknown as string, outputFormat: "MMM YYYY" });
      },
    },
    {
      id: "line_item_marketing_fee_rate",
      accessorKey: "line_item_marketing_fee_rate",
      header: "Marketing Fee Rate",
      minSize: 160,
      maxSize: 160,
      aggregationFn: () => "",
      aggregatedCell: () => "",
    },
    {
      id: "line_item_futures",
      accessorKey: "line_item_futures",
      header: "Futures",
      minSize: 160,
      maxSize: 160,
      cell: (info) => formatNumberToDisplay(info.getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
      aggregationFn: () => "",
      aggregatedCell: () => "",
    },
    {
      id: "line_item_basis",
      accessorKey: "line_item_basis",
      header: "Basis",
      minSize: 160,
      maxSize: 160,
      cell: (info) => formatNumberToDisplay(info.getValue() as Decimal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      aggregationFn: () => "",
      aggregatedCell: () => "",
    },
    {
      id: "offset_against_ap_settlement",
      accessorKey: "offset_against_ap_settlement",
      accessorFn: (row) => row.offset_against_ap_settlement ? "Yes" : "No",
      header: "Offset Against AP Settlement",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "offset_against_ap_settlement") ? "Yes" : "No",
      minSize: 160,
      maxSize: 160,
    }, 
    {
      id: "should_send_ar_invoice",
      accessorKey: "should_send_ar_invoice",
      accessorFn: (row) => row.should_send_ar_invoice ? "Yes" : "No",
      header: "Should Send AR Invoice",
      aggregationFn: parentFieldAggregation,
      aggregatedCell: ({ row }) => getParentField(row, "should_send_ar_invoice") ? "Yes" : "No",
      minSize: 160,
      maxSize: 160,
    }
  ];

  const openedColumns = generateColumns("closed");
  const closedColumns = generateColumns("open");

  const lineItemRowBgColor = (row: FlattenedDirectSaleLineItem): RowBgColorResult | undefined => {
    if (row._parentDirectSale.has_placeholder_data ?? false) {
      return { preset: 'light-orange' };
    }
    return { preset: 'light-blue' };
  };

  const legend: DataTableLegendItem[] = [
    { color: { preset: 'light-blue' }, label: 'Flat' },
    { color: { preset: 'light-orange' }, label: 'Other' },
  ];

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
        {activeTab === "open" ? "Open Direct Sales" : activeTab === "closed" ? "Closed Direct Sales" : "Direct Sales Summary"}
      </h1>
      <div className="flex justify-end gap-4 mb-4">
        <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2">
              <ListFilterIcon className="h-4 w-4" />
              Filter
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] md:max-w-[700px]">
            <DialogHeader>
              <DialogTitle>Filter Direct Sales</DialogTitle>
              <DialogDescription>
                Select criteria to filter the direct sales entries.
              </DialogDescription>
            </DialogHeader>
            <form className="grid gap-4 py-4" onSubmit={handleApplyFilters}>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="filter-producer" className="text-right">
                  Producer:
                </Label>
                <Combobox
                  dwimOnChange
                  virtualizeList
                  popoverClassName="w-[400px] h-[200px]"
                  isModal
                  id="filter-producer"
                  className="col-span-3 w-full"
                  value={tempFilters.producer_id || ""}
                  onValueChange={(value) => setTempFilters({ ...tempFilters, producer_id: value === "" ? undefined : value })}
                  options={genIdOptionList(customers, "long_name")}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="filter-commodity" className="text-right">
                  Commodity:
                </Label>
                <Combobox
                  dwimOnChange
                  isModal
                  id="filter-commodity"
                  className="col-span-3 w-full"
                  value={tempFilters.commodity || ""}
                  onValueChange={(value) => setTempFilters({ ...tempFilters, commodity: value === "" ? undefined : value })}
                  options={CommodityOptions}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="filter-futures-month" className="text-right">
                  Futures Month:
                </Label>
                <Combobox
                  dwimOnChange
                  isModal
                  id="filter-futures-month"
                  className="col-span-3 w-full"
                  value={tempFilters.futures_month || ""}
                  onValueChange={(value) => setTempFilters({ ...tempFilters, futures_month: value === "" ? undefined : value })}
                  options={formulateOptionMonths()}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="filter-gl-date" className="text-right">
                  GL Date:
                </Label>
                <DatePicker
                  value={tempFilters.gl_date ? moment(tempFilters.gl_date).toDate() : undefined}
                  onChange={(date) => setTempFilters({ ...tempFilters, gl_date: date ? moment(date).format('YYYY-MM-DD') : undefined })}
                  placeholder="MM/DD/YYYY"
                  className="col-span-3 w-full"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="filter-entry-start" className="text-right">
                  Entry Start:
                </Label>
                <DatePicker
                  value={tempFilters.entry_start_date ? moment(tempFilters.entry_start_date).toDate() : undefined}
                  onChange={(date) => setTempFilters({ ...tempFilters, entry_start_date: date ? moment(date).format('YYYY-MM-DD') : undefined })}
                  placeholder="MM/DD/YYYY"
                  className="col-span-3 w-full"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="filter-entry-end" className="text-right">
                  Entry End:
                </Label>
                <DatePicker
                  value={tempFilters.entry_end_date ? moment(tempFilters.entry_end_date).toDate() : undefined}
                  onChange={(date) => setTempFilters({ ...tempFilters, entry_end_date: date ? moment(date).format('YYYY-MM-DD') : undefined })}
                  minDate={tempFilters.entry_start_date ? moment(tempFilters.entry_start_date).toDate() : undefined}
                  placeholder="MM/DD/YYYY"
                  className="col-span-3 w-full"
                />
              </div>
            </form>
            <DialogFooter>
              <Button variant="outline" onClick={handleResetFilters}>
                Reset Filters
              </Button>
              <Button onClick={() => handleApplyFilters()}>Apply Filters</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <RecentReportDialog />
        <BreakdownReportDialog status={undefined} />
        <Button variant="outline" className="gap-2" onClick={() => setOpenReportDialogOpen(true)}>
          Generate Open Report
        </Button>
        <OpenReportDialog open={openReportDialogOpen} onOpenChange={setOpenReportDialogOpen} />
        <CreateFormModal
          locations={locations}
          customers={customers}
          traders={traders}
        />
        <UpdateFormModal
          traders={traders}
          locations={locations}
          customers={customers}
          isOpen={!!updateFormData}
          updatedFormData={updateFormData}
          onDialogChange={(value: any) => {
            if (!value) {
              setUpdateFormData(null);
            }
          }}
        />
        <ConfirmDeleteModal directSale={deletingDirectSaleData} onDeleted={() => setDeletingDirectSaleData(undefined)} />
      </div>
      {Object.keys(filters).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <span className="font-semibold text-sm mr-2">Active Filters:</span>
          {filters.producer_id && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Producer: {getCustomerNameById(filters.producer_id)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('producer_id')} />
            </Badge>
          )}
          {filters.commodity && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Commodity: {getCommodityLabel(filters.commodity)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('commodity')} />
            </Badge>
          )}
          {filters.futures_month && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Futures Month: {getFuturesMonthLabel(filters.futures_month)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('futures_month')} />
            </Badge>
          )}
          {filters.gl_date && (
            <Badge variant="secondary" className="flex items-center gap-1">
              GL Date: {formatMonthForDisplay(filters.gl_date)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('gl_date')} />
            </Badge>
          )}
          {filters.entry_start_date && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Entry Start: {formatDateForDisplay(filters.entry_start_date)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('entry_start_date')} />
            </Badge>
          )}
          {filters.entry_end_date && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Entry End: {formatDateForDisplay(filters.entry_end_date)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('entry_end_date')} />
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-6 px-2 text-xs"
          >
            Clear All
          </Button>
        </div>
      )}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "summary" | "open" | "closed")} className="space-y-6">
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
        </TabsList>
        <TabsContent value="summary">
          <SummaryTable summary={summary} />
        </TabsContent>
        <TabsContent value="open">
          <DataTable
            className={`max-h-[calc(100vh-500px)] overflow-y-auto`}
            data={flattenedContracts}
            columns={openedColumns}
            enableMultiFilter
            enableColumnPinning
            enableMultiSelect
            defaultCondensed
            useSteppedGroupingExpansion
            stickyHeaders
            enableColumnVisibility
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            groupBy={["direct_sale_id"]}
            virtualizationRowHeight={49}
            virtualizationOverscan={2}
            enableVirtualization
            enableColumnResizing
            enableExport
            filterStyle='select'
            filterMode='internal'
            emptyMessage={"No Direct Sales"}
            rowClickable
            defaultSorting={[
              { id: "contract_date", desc: true },
            ]}
            onRowClick={(item) => {
              setUpdateFormData(item._parentDirectSale);
            }}
            rowBgColor={lineItemRowBgColor}
            legend={legend}
          />
        </TabsContent>
        <TabsContent value="closed">
          <DataTable
            className={`max-h-[calc(100vh-500px)] overflow-y-auto`}
            data={flattenedContracts}
            columns={closedColumns}
            stickyHeaders
            enableColumnPinning
            enableMultiFilter
            enableMultiSelect
            defaultCondensed
            useSteppedGroupingExpansion
            enableColumnVisibility
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            groupBy={["direct_sale_id"]}
            defaultColumnVisibility={{ direct_sale_id: false }}
            virtualizationRowHeight={49}
            virtualizationOverscan={2}
            enableVirtualization
            enableColumnResizing
            enableExport
            filterStyle='select'
            filterMode='internal'
            emptyMessage={"No Direct Sales"}
            rowClickable
            defaultSorting={[
              { id: "contract_date", desc: true },
            ]}
            onRowClick={(item) => {
              setUpdateFormData(item._parentDirectSale);
            }}
            rowBgColor={lineItemRowBgColor}
            legend={legend}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}
